import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Modal, OptionsType, Select } from "@/components";
import { toastError, toastSuccess } from "@/utils";
import { DEFAULT_ERROR_MESSAGE, DEFAULT_SUCCESS_MESSAGE } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { storeService } from "@/services/storeService";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleClose: () => void;
  userName: string;
  userId: number;
  defaultStore: number;
  className?: string;
  onRefresh: () => void;
}

export const TransferUserModal = ({
  open,
  setOpen,
  handleClose,
  userId,
  userName,
  defaultStore,
  onRefresh,
}: Props) => {
  const [selectedStore, setSelectedStore] = useState<number>(defaultStore);
  const [loading, setLoading] = useState(false);
  const { role, user } = useAuth();
  const companyId = Number(user?.company_id);

  const { data: myStores, refetch: refetchMyStoresQuery } = useQuery({
    queryKey: ["my-stores", user?.id],
    queryFn: () => {
      return (async () => {
        const res = await storeService.getMyStores();
        return (res.data || []).map((store) => ({
          value: store.key,
          label: store.label,
        }));
      })();
    },
    enabled: false,
  });

  const loadOptions = async () => {
    if (!companyId || !role || !open) return;
    setLoading(true);
    await refetchMyStoresQuery();
    setLoading(false);
  };

  const mutateMoveStoreStore = useMutation({
    mutationFn: (data: { userId: number; storeId: number }) => {
      return storeService.moveUserToStore(data);
    },
    onSuccess: (resp) => {
      toastSuccess(resp?.message || DEFAULT_SUCCESS_MESSAGE);
      handleClose();
      onRefresh();
    },
    onError: (err: any) => {
      toastError(err?.message || DEFAULT_ERROR_MESSAGE);
    },
  });

  useEffect(() => {
    loadOptions();
  }, [companyId, open]);

  useEffect(() => {
    setSelectedStore(defaultStore);
  }, [defaultStore]);

  const selectedStoreOption = useMemo(() => {
    return myStores?.find((store) => store.value === selectedStore);
  }, [myStores, selectedStore]);

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      title={`「${userName}」を移動`}
      overflowScroll={false}
    >
      <Select
        label="店舗名"
        placeholder="移動したい先を選択してください"
        isLoading={loading}
        options={myStores as OptionsType}
        value={selectedStoreOption as any}
        onChange={(data) => {
          setSelectedStore(data?.value || null);
        }}
      />

      <div className="flex justify-end gap-4 mt-4">
        <Button
          buttonType="text"
          className="!h-12"
          type="button"
          onClick={handleClose}
          disabled={loading}
        >
          キャンセル
        </Button>

        <Button
          buttonType="primary"
          className="!h-12"
          type="submit"
          isLoading={loading}
          onClick={() => {
            mutateMoveStoreStore.mutate({
              userId,
              storeId: selectedStore || 0,
            });
          }}
        >
          移動
        </Button>
      </div>
    </Modal>
  );
};
